import { assignInlineVars } from "@vanilla-extract/dynamic";
import clsx from "clsx";
import React from "react";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import {
    buildUrl,
    Image,
} from "web/react/pages/product/product-image-gallery/product-gallery-image";
import {
    DESKTOP_THUMBNAIL_ELEMENT_SIZE,
    MAX_VISIBLE_THUMBNAILS,
    MOBILE_THUMBNAIL_ELEMENT_SIZE,
    THUMBNAIL_STRIP_GAP,
    THUMBNAIL_URL_SIZE,
} from "./thumbnail-strip.constants";
import * as styles from "./thumbnail-strip.css";

const calculateThumbnailWindowStart = (currentIndex: number, totalCount: number): number => {
    if (totalCount <= MAX_VISIBLE_THUMBNAILS || currentIndex < MAX_VISIBLE_THUMBNAILS) {
        return 0;
    }

    const maxStart = totalCount - MAX_VISIBLE_THUMBNAILS;
    return Math.max(0, Math.min(currentIndex - (MAX_VISIBLE_THUMBNAILS - 1), maxStart));
};

interface ThumbnailImageProps {
    image: string;
    height: number;
    width: number;
    onClick?: () => void;
    isActive?: boolean;
}

function ThumbnailImage({
    image,
    height,
    width,
    onClick,
    isActive = false,
}: ThumbnailImageProps): React.ReactElement {
    const src = buildUrl(image, THUMBNAIL_URL_SIZE.width, THUMBNAIL_URL_SIZE.height);

    return (
        <button
            type="button"
            onClick={onClick}
            onFocus={onClick}
            className={styles.thumbnailButton}
        >
            <img
                className={clsx(styles.thumbnailImage, {
                    [styles.thumbnailImageActive]: isActive,
                })}
                src={src}
                height={height}
                width={width}
                alt=""
            />
        </button>
    );
}

interface MoreProductsThumbnailProps {
    images: string[];
    height: number;
    width: number;
    isActive?: boolean;
    onClick?: () => void;
}

function MoreProductsThumbnail({
    images,
    height,
    width,
    isActive = false,
    onClick,
}: MoreProductsThumbnailProps): React.ReactElement {
    return (
        <button
            type="button"
            onClick={onClick}
            onFocus={onClick}
            className={styles.thumbnailButton}
        >
            <div
                className={clsx(styles.mosaicWrapper, {
                    [styles.mosaicWrapperActive]: isActive,
                })}
                style={{ width, height }}
            >
                {images.slice(0, 4).map((src, i) => (
                    <img key={i} src={src} alt="" className={styles.mosaicImage} />
                ))}
            </div>
        </button>
    );
}

interface ThumbnailStripProps {
    images: Image[];
    currentSlideIndex: number;
    onThumbnailImageClick: (index: number) => void;
    isMobileInline?: boolean;
    moreProductsImages?: string[];
}

export function ThumbnailStrip({
    images,
    currentSlideIndex,
    onThumbnailImageClick,
    isMobileInline = false,
    moreProductsImages,
}: ThumbnailStripProps): React.ReactElement {
    const { isDesktopViewport, isTabletViewport } = useDomViewport();
    const isVerticalStrip = isTabletViewport || isDesktopViewport;
    const thumbnailHeight = isVerticalStrip
        ? DESKTOP_THUMBNAIL_ELEMENT_SIZE.height
        : MOBILE_THUMBNAIL_ELEMENT_SIZE.height;
    const thumbnailWidth = isVerticalStrip
        ? DESKTOP_THUMBNAIL_ELEMENT_SIZE.width
        : MOBILE_THUMBNAIL_ELEMENT_SIZE.width;

    const totalCount = images.length + (moreProductsImages ? 1 : 0);
    const windowStart = calculateThumbnailWindowStart(currentSlideIndex, totalCount);
    const thumbnailStep =
        (isVerticalStrip ? thumbnailHeight : thumbnailWidth) + THUMBNAIL_STRIP_GAP;
    const stripOffset = windowStart * thumbnailStep;

    return (
        <div
            className={clsx(styles.thumbnailStripWrapper, {
                [styles.thumbnailStripWrapperMobileInline]: isMobileInline,
            })}
        >
            <div
                className={styles.thumbnailViewport}
                style={assignInlineVars({
                    [styles.visibleThumbnails]: Math.min(
                        totalCount,
                        MAX_VISIBLE_THUMBNAILS
                    ).toString(),
                })}
            >
                <ul
                    className={styles.thumbnailTrack}
                    style={assignInlineVars({
                        [styles.transform]: isVerticalStrip
                            ? `translateY(-${stripOffset}px)`
                            : `translateX(-${stripOffset}px)`,
                    })}
                >
                    {images.map((image, index) => (
                        <li key={`${image.id}-${index}`} className={styles.thumbnailItem}>
                            <ThumbnailImage
                                image={image.url}
                                height={thumbnailHeight}
                                width={thumbnailWidth}
                                onClick={() => onThumbnailImageClick(index)}
                                isActive={index === currentSlideIndex}
                            />
                        </li>
                    ))}
                    {moreProductsImages && (
                        <li key="more-products" className={styles.thumbnailItem}>
                            <MoreProductsThumbnail
                                images={moreProductsImages}
                                height={thumbnailHeight}
                                width={thumbnailWidth}
                                onClick={() => onThumbnailImageClick(images.length)}
                                isActive={currentSlideIndex === images.length}
                            />
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
}
